import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { CONSTANTS } from "../data";

interface XPBarProps {
  xp: number;
  level: number;
  onToggleTooltip: () => void;
  isOpen: boolean;
}

const XPBar: React.FC<XPBarProps> = ({ xp, level, onToggleTooltip, isOpen }) => {
  const xpForCurrentLevel = CONSTANTS.LEVEL_XP_THRESHOLDS[level] ?? 0;
  const xpForNextLevel = CONSTANTS.LEVEL_XP_THRESHOLDS[level + 1];

  const progress = useMemo(() => {
    if (xpForNextLevel === undefined) return 1;
    const xpInLevel = xp - xpForCurrentLevel;
    const xpNeededForLevel = xpForNextLevel - xpForCurrentLevel;
    return Math.max(0, Math.min(1, xpInLevel / xpNeededForLevel));
  }, [xp, level, xpForCurrentLevel, xpForNextLevel]);

  const upcomingUnlocks = useMemo(() => {
    const nextLevel = level + 1;
    const itemUnlocks = CONSTANTS.LEVEL_UNLOCKS[nextLevel]?.items;
    const tutorialUnlocks = Object.values(CONSTANTS.TUTORIALS).filter(
      (t) => t.levelGate === nextLevel
    );

    const unlocks = [];
    if (itemUnlocks) {
      unlocks.push(
        ...Object.keys(itemUnlocks).map(
          (id) => `Item: ${CONSTANTS.ITEMS[id]?.name || id}`
        )
      );
    }
    if (tutorialUnlocks.length > 0) {
      unlocks.push(...tutorialUnlocks.map((t) => `Tutorial: ${t.title}`));
    }
    return unlocks;
  }, [level]);

  return (
    <div className="xp-bar-container flex items-center gap-2 xp-bar-tooltip-trigger tooltip-trigger cursor-pointer" onClick={onToggleTooltip}>
      <span className="font-bold text-sm sm:text-base">Lv.{level}</span>
      <div className="w-24 sm:w-32 h-3 bg-black bg-opacity-20 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-accent rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>
      <span className="text-xs sm:text-sm font-mono opacity-80">
        {xpForNextLevel !== undefined ? `${xp}/${xpForNextLevel}` : `${xp} XP`}
      </span>
      {upcomingUnlocks.length > 0 && (
        <div className={`xp-bar-tooltip ${isOpen ? 'tooltip-visible' : ''}`}>
          <h4 className="font-bold mb-1 border-b border-gray-500 pb-1">
            Next Level Unlocks:
          </h4>
          <ul className="list-disc list-inside text-xs mt-1 space-y-1">
            {upcomingUnlocks.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default XPBar;
